import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Search,
  MoreHorizontal,
  Trash2,
  FolderPlus,
  Check,
  Share2,
  SquarePen,
  MessageSquare,
} from 'lucide-react';
import { ASK_PATH, askSessionPath } from '@/constants/appRoutes';
import { chatApi, projectsApi, usersApi, SharedUser } from '@/services/api';
import { useChatSessions } from '@/hooks/useChatSessions';
import { useProjects } from '@/hooks/useProjects';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ShellAccountMenu } from '@/features/shell/ShellAccountMenu';
import { ShellSidebarSlot } from '@/contexts/ShellSidebarContext';
import { cn } from '@/lib/utils';

type FilterKind = 'all' | 'mine' | 'shared';

const FILTER_OPTIONS: { id: FilterKind; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'mine', label: 'Mine' },
  { id: 'shared', label: 'Shared' },
];

function formatTitle(title: string | null) {
  if (!title || !title.trim()) return 'New conversation';
  return title.length > 60 ? `${title.slice(0, 60)}…` : title;
}

interface SessionRowProps {
  session: {
    id: string;
    title: string | null;
    is_owner?: boolean;
    role?: string | null;
  };
  active: boolean;
  onOpen: (id: string) => void;
  onAddToProject?: (id: string) => void;
  onShare?: (id: string) => void;
  onRemove?: (id: string) => void;
}

function SessionRow({
  session,
  active,
  onOpen,
  onAddToProject,
  onShare,
  onRemove,
}: SessionRowProps) {
  return (
    <div
      className={cn(
        'group flex min-h-11 min-w-0 items-center gap-1 rounded-lg transition-colors',
        active ? 'bg-[var(--rcm-color-selected)]' : 'hover:bg-[var(--rcm-color-selected)]',
      )}
    >
      <button
        type="button"
        onClick={() => onOpen(session.id)}
        className="flex min-h-11 min-w-0 flex-1 items-center gap-2.5 overflow-hidden px-2 text-left"
      >
        <MessageSquare className="h-4 w-4 shrink-0 text-[var(--rcm-color-text-secondary)]" strokeWidth={1.75} aria-hidden />
        <div className="min-w-0 flex-1 overflow-hidden">
          <p
            className="truncate text-sm font-medium text-foreground"
            title={session.title ?? 'New conversation'}
          >
            {formatTitle(session.title)}
          </p>
          {!session.is_owner && (
            <p className="truncate text-xs capitalize text-muted-foreground">
              {session.role ?? 'shared'}
            </p>
          )}
        </div>
      </button>
      {session.is_owner && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Conversation actions"
              className={cn(
                'mr-0.5 h-9 w-9 shrink-0',
                active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onAddToProject?.(session.id)}>
              <FolderPlus className="mr-2 h-4 w-4" />
              Add to project
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onShare?.(session.id)}>
              <Share2 className="mr-2 h-4 w-4" />
              Share
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => onRemove?.(session.id)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Remove from list
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function SessionSection({
  label,
  count,
  sessions,
  activeSessionId,
  onOpen,
  onAddToProject,
  onShare,
  onRemove,
}: {
  label: string;
  count?: number;
  sessions: SessionRowProps['session'][];
  activeSessionId?: string;
  onOpen: (id: string) => void;
  onAddToProject: (id: string) => void;
  onShare: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  if (sessions.length === 0) return null;

  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between px-3 py-1.5">
        <p className="text-[13px] font-medium text-muted-foreground">{label}</p>
        {count !== undefined && (
          <span className="text-xs tabular-nums text-muted-foreground/80">{count}</span>
        )}
      </div>
      <div className="min-w-0 space-y-0.5">
        {sessions.map((session) => (
          <SessionRow
            key={session.id}
            session={session}
            active={session.id === activeSessionId}
            onOpen={onOpen}
            onAddToProject={onAddToProject}
            onShare={onShare}
            onRemove={onRemove}
          />
        ))}
      </div>
    </div>
  );
}

interface ChatSessionListProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface SearchUser {
  id: string;
  username: string;
  full_name?: string | null;
}

export function ChatSessionList({ mobileOpen = false, onMobileClose }: ChatSessionListProps) {
  const { sessionId: activeSessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const { sessions, loading, refresh: refreshSessions } = useChatSessions();
  const { allProjects, loading: projectsLoading } = useProjects();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKind>('all');
  const [addToProjectSessionId, setAddToProjectSessionId] = useState<string | null>(null);
  const [togglingProject, setTogglingProject] = useState<string | null>(null);
  const [sessionProjects, setSessionProjects] = useState<Set<string>>(new Set());
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [sharedWith, setSharedWith] = useState<SharedUser[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [addableUsers, setAddableUsers] = useState<SearchUser[]>([]);
  const [addUserRole, setAddUserRole] = useState<'editor' | 'viewer'>('viewer');
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [hiddenSessionIds, setHiddenSessionIds] = useState<Set<string>>(new Set());

  const filteredSessions = sessions.filter((s) => {
    if (hiddenSessionIds.has(s.id)) return false;
    const matchSearch =
      !search.trim() || (s.title || '').toLowerCase().includes(search.trim().toLowerCase());
    if (!matchSearch) return false;
    if (filter === 'mine') return s.is_owner === true;
    if (filter === 'shared') return s.is_owner === false;
    return true;
  });

  const openSession = (id: string) => {
    navigate(askSessionPath(id));
    onMobileClose?.();
  };

  const handleOpenAddToProject = (sessionId: string) => {
    setAddToProjectSessionId(sessionId);
    const session = sessions.find((s) => s.id === sessionId);
    setSessionProjects(new Set(session?.project_ids ?? []));
  };

  const handleToggleProject = async (projectId: string) => {
    if (!addToProjectSessionId || togglingProject) return;
    setTogglingProject(projectId);
    const isLinked = sessionProjects.has(projectId);
    try {
      if (isLinked) {
        await projectsApi.removeChat(projectId, addToProjectSessionId);
        setSessionProjects((prev) => {
          const next = new Set(prev);
          next.delete(projectId);
          return next;
        });
      } else {
        await projectsApi.addChat(projectId, addToProjectSessionId);
        setSessionProjects((prev) => new Set([...prev, projectId]));
      }
    } finally {
      setTogglingProject(null);
    }
  };

  const getInitials = (fullName?: string | null, username?: string) => {
    if (fullName) {
      const parts = fullName.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return parts[0][0]?.toUpperCase() || '?';
    }
    return username?.[0]?.toUpperCase() || '?';
  };

  const loadSharedWith = async (sessionId: string) => {
    try {
      const data = await chatApi.getSharedWith(sessionId);
      setSharedWith(data);
    } catch (error) {
      console.error('Error loading shared users:', error);
      setSharedWith([]);
    }
  };

  useEffect(() => {
    if (!shareDialogOpen || !userSearchQuery.trim()) {
      setAddableUsers([]);
      return;
    }

    const timer = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const results = await usersApi.search(userSearchQuery);
        const sharedUserIds = new Set(sharedWith.map((u) => u.user_id));
        const filtered = results
          .filter((u) => !sharedUserIds.has(u.id))
          .map((u) => ({ id: u.id, username: u.username, full_name: u.full_name }));
        setAddableUsers(filtered);
      } catch (error) {
        console.error('User search error:', error);
        setAddableUsers([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [userSearchQuery, shareDialogOpen, sharedWith]);

  const handleOpenShare = async (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setShareDialogOpen(true);
    setUserSearchQuery('');
    setAddUserError(null);
    await loadSharedWith(sessionId);
  };

  const handleCloseShare = () => {
    setShareDialogOpen(false);
    setSelectedSessionId(null);
    setSharedWith([]);
    setUserSearchQuery('');
    setAddableUsers([]);
    setAddUserError(null);
  };

  const handleAddUser = async (user: SearchUser) => {
    if (!selectedSessionId) return;
    setAddUserError(null);
    try {
      await chatApi.addSharedWith(selectedSessionId, user.id, addUserRole);
      await loadSharedWith(selectedSessionId);
      void refreshSessions();
      setUserSearchQuery('');
      setAddableUsers([]);
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to add user';
      setAddUserError(msg);
      console.error('Error adding user:', error);
    }
  };

  const handleUpdateSharedRole = async (userId: string, role: 'editor' | 'viewer') => {
    if (!selectedSessionId) return;
    try {
      await chatApi.updateSharedWithRole(selectedSessionId, userId, role);
      await loadSharedWith(selectedSessionId);
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const handleRemoveSharedWith = async (userId: string) => {
    if (!selectedSessionId) return;
    try {
      await chatApi.removeSharedWith(selectedSessionId, userId);
      await loadSharedWith(selectedSessionId);
    } catch (error) {
      console.error('Error removing user:', error);
    }
  };

  const sharedSessions = filteredSessions.filter((s) => s.is_owner === false);
  const mineSessions = filteredSessions.filter((s) => s.is_owner !== false);

  const handleRemoveFromList = (sessionId: string) => {
    setHiddenSessionIds((prev) => new Set(prev).add(sessionId));
  };

  const listBody = (
    <>
      <div className="flex-shrink-0 space-y-3 px-3 pb-2 pt-3">
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full justify-start gap-2 rounded-lg border-[var(--rcm-color-border)] bg-[var(--rcm-color-surface)] text-sm font-medium text-foreground shadow-none hover:bg-[var(--rcm-color-surface)]"
          onClick={() => {
            navigate(ASK_PATH);
            onMobileClose?.();
          }}
        >
          <SquarePen className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          New conversation
        </Button>
        <div className="relative min-w-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--rcm-color-text-secondary)]" aria-hidden />
          <Input
            placeholder="Search conversations..."
            aria-label="Search conversations"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-10 rounded-lg border-[var(--rcm-color-border)] bg-[var(--rcm-color-surface)] pl-9 text-sm shadow-none"
          />
        </div>
        <div className="flex items-center gap-1" role="group" aria-label="Filter conversations">
          {FILTER_OPTIONS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              aria-pressed={filter === id}
              className={cn(
                'h-9 rounded-lg px-3 text-sm transition-colors',
                filter === id
                  ? 'bg-[var(--rcm-color-selected)] font-medium text-[var(--rcm-color-primary)]'
                  : 'text-[var(--rcm-color-text-secondary)] hover:text-foreground',
              )}
              onClick={() => setFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <ScrollArea className="chat-sessions-panel min-h-0 min-w-0 flex-1">
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner className="h-6 w-6" />
          </div>
        ) : filteredSessions.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-muted-foreground">No conversations yet.</p>
        ) : filter === 'all' ? (
          <div className="min-w-0 max-w-full space-y-3 overflow-hidden p-2">
            <SessionSection
              label="Recent"
              count={mineSessions.length}
              sessions={mineSessions}
              activeSessionId={activeSessionId}
              onOpen={openSession}
              onAddToProject={handleOpenAddToProject}
              onShare={handleOpenShare}
              onRemove={handleRemoveFromList}
            />
            <SessionSection
              label="Shared with me"
              count={sharedSessions.length}
              sessions={sharedSessions}
              activeSessionId={activeSessionId}
              onOpen={openSession}
              onAddToProject={handleOpenAddToProject}
              onShare={handleOpenShare}
              onRemove={handleRemoveFromList}
            />
          </div>
        ) : (
          <div className="min-w-0 max-w-full space-y-0.5 overflow-hidden p-2">
            {filteredSessions.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                active={session.id === activeSessionId}
                onOpen={openSession}
                onAddToProject={handleOpenAddToProject}
                onShare={handleOpenShare}
                onRemove={handleRemoveFromList}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </>
  );

  const sidebarColumn = (
    <div className="flex h-full min-h-0 flex-col">
      {listBody}
      <div className="mt-auto flex-shrink-0 border-t border-[var(--rcm-color-border)] p-3">
        <ShellAccountMenu />
      </div>
    </div>
  );

  return (
    <>
      <ShellSidebarSlot>{sidebarColumn}</ShellSidebarSlot>

      {mobileOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            aria-label="Close conversations"
            onClick={onMobileClose}
          />
          <aside className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-[var(--rcm-color-sidebar)] shadow-xl lg:hidden">
            {sidebarColumn}
          </aside>
        </>
      )}

      <Dialog open={!!addToProjectSessionId} onOpenChange={(open) => !open && setAddToProjectSessionId(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add to project</DialogTitle>
            <DialogDescription>Link this chat to a project.</DialogDescription>
          </DialogHeader>
          {projectsLoading ? (
            <div className="flex justify-center py-6">
              <Spinner className="h-6 w-6" />
            </div>
          ) : (
            <ScrollArea className="max-h-64">
              <div className="space-y-1">
                {allProjects.map((project) => {
                  const isLinked = sessionProjects.has(project.id);
                  return (
                    <button
                      key={project.id}
                      type="button"
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-accent/60"
                      onClick={() => handleToggleProject(project.id)}
                      disabled={!!togglingProject}
                    >
                      <span className="text-sm font-medium truncate">{project.name}</span>
                      {isLinked && <Check className="h-4 w-4 text-primary" />}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddToProjectSessionId(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shareDialogOpen} onOpenChange={handleCloseShare}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage shared access</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold mb-2">Currently shared with</h3>
              {sharedWith.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">Only you have access. Add people below.</p>
              ) : (
                <div className="space-y-2">
                  {sharedWith.map((u) => (
                    <div
                      key={u.user_id}
                      className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="bg-muted text-sm">
                            {getInitials(u.full_name, u.username)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium">{u.full_name || u.username}</p>
                          <p className="text-xs text-muted-foreground">{u.username}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={u.role}
                          onValueChange={(val) =>
                            handleUpdateSharedRole(u.user_id, val as 'editor' | 'viewer')
                          }
                        >
                          <SelectTrigger className="w-[100px] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="viewer">Viewer</SelectItem>
                            <SelectItem value="editor">Editor</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveSharedWith(u.user_id)}
                          className="text-destructive hover:text-destructive"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-2">Add people</h3>
              <div className="flex items-center gap-2 mb-2">
                <Label htmlFor="chat-list-share-role-select" className="text-sm">
                  Role:
                </Label>
                <Select
                  value={addUserRole}
                  onValueChange={(val) => setAddUserRole(val as 'editor' | 'viewer')}
                >
                  <SelectTrigger id="chat-list-share-role-select" className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input
                placeholder="Search by username or name..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="mb-2"
              />
              {addUserError && <p className="text-sm text-destructive mb-2">{addUserError}</p>}
              {userSearchLoading ? (
                <div className="flex justify-center py-4">
                  <Spinner className="h-6 w-6" />
                </div>
              ) : addableUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">
                  {userSearchQuery.trim() ? 'No users found or already added.' : 'Type to search for users.'}
                </p>
              ) : (
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {addableUsers.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-accent"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-muted text-xs">
                            {getInitials(u.full_name, u.username)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium">{u.full_name || u.username}</p>
                          <p className="text-xs text-muted-foreground">{u.username}</p>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => handleAddUser(u)}>
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCloseShare}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
